import React, { useState, useEffect, useCallback } from "react";
import "./bankList.css";

import BankHeader from "../component/bankHeader";
import ModalDeleteConfirmation from "../../../components/modal/modalDeleteConfirmation";
import BankTable from "../component/bankTable";
import PaginationFooter from "../../../components/footer/paginationFooter";
import ModalAddBank from "../component/modalAddBank";
import { Constant } from "../../../helper/constant";
import { useNavigate } from "react-router-dom";

const BankList = () => {
  // =====================
  // Constant Definition
  // =====================
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [isOpenModalAddBank, setIsOpenModalAddBank] = useState(false);
  const [selectedBankData, setSelectedBankData] = useState({
    bank_id: "",
    bank_name: "",
  });
  const [bankData, setBankData] = useState(null);
  const [isOpenModalDeleteBank, setIsOpenModalDeleteBank] = useState(false);
  const [idBankToDelete, setIdBankToDelete] = useState("");
  const [searchBankKeyword, setSearchBankKeyword] = useState("");

  // =====================
  // Function Definition
  // =====================

  const addNewBank = () => {
    resetSelectedBankDataValue();
    setIsOpenModalAddBank(true);
  };

  const onEditBank = (bankData) => {
    setSelectedBankData(bankData);
    setIsOpenModalAddBank(true);
  };

  const onDeleteBank = (bankData) => {
    setIsOpenModalDeleteBank(true);
    setIdBankToDelete(bankData.bank_id);
  };

  const onDeleteBankConfirmed = () => {
    setIsOpenModalDeleteBank(false);
    deleteBank();
  };

  const onCloseModalDeleteBank = () => {
    setIdBankToDelete("");
    setIsOpenModalDeleteBank(false);
  };

  const onCloseModal = () => {
    resetSelectedBankDataValue();
    setIsOpenModalAddBank(false);
  };

  const onSubmitNewBank = (bankData, isEditData) => {
    setIsOpenModalAddBank(false);
    if (isEditData) {
      editBank(bankData);
    } else {
      createNewBank(bankData);
    }
  };

  const resetSelectedBankDataValue = () => {
    setSelectedBankData({
      bank_id: "",
      bank_name: "",
    });
  };

  const onNextPageClicked = () => {
    if (page < totalPage) {
      setPage((prevPage) => {
        return prevPage + 1;
      });
    }
  };

  const onPreviousPageClicked = () => {
    if (page !== 1) {
      setPage((prevPage) => {
        return prevPage - 1;
      });
    }
  };

  // =====================
  // Request Method
  // =====================

  const getBankData = useCallback(async () => {
    try {
      setIsLoading(true);

      let url = Constant.BASE_API_URL + "/bank?page=" + page;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "bearer " + localStorage.getItem("token"),
        },
      });
      if (!response.ok) {
        alert("Gagal Menerima Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
      const json = await response.json();
      const data = json.data;
      if (!data.banks) {
        setBankData(null);
      } else {
        setBankData(data);
      }
      const totalPage = data.meta.total_page;
      setTotalPage(totalPage);
    } catch (error) {
      setBankData(null);
    } finally {
      setIsLoading(false);
    }
  }, [page, navigate]);

  const createNewBank = async (bankData) => {
    try {
      setIsLoading(true);
      const response = await fetch(Constant.BASE_API_URL + "/bank", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "bearer " + localStorage.getItem("token"),
        },
        body: JSON.stringify({
          bank_name: bankData.bank_name,
        }),
      });
      if (!response.ok) {
        alert("Terdapat Kesalahan Dalam Memproses Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
    } finally {
      getBankData();
    }
  };

  const editBank = async (bankData) => {
    try {
      setIsLoading(true);
      const bankId = bankData.bank_id;
      const response = await fetch(Constant.BASE_API_URL + "/bank/" + bankId, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: "bearer " + localStorage.getItem("token"),
        },
        body: JSON.stringify({
          bank_name: bankData.bank_name,
        }),
      });
      if (!response.ok) {
        alert("Gagal Mengubah Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
    } finally {
      getBankData();
    }
  };

  const deleteBank = async () => {
    try {
      setIsLoading(true);
      const bankId = idBankToDelete;
      const response = await fetch(Constant.BASE_API_URL + "/bank/" + bankId, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: "bearer " + localStorage.getItem("token"),
        },
      });
      if (!response.ok) {
        alert("Gagal Menghapus Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
    } finally {
      setIdBankToDelete("");
      getBankData();
    }
  };

  const searchBank = (searchText) => {
    setSearchBankKeyword(searchText);
    setPage(1);
  };

  const getBankSearchData = useCallback(async () => {
    try {
      setIsLoading(true);

      let url = new URL(Constant.BASE_API_URL + "/bank/search");
      url.searchParams.append("keyword", searchBankKeyword);
      url.searchParams.append("page", page);

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "bearer " + localStorage.getItem("token"),
        },
      });
      if (!response.ok) {
        alert("Gagal Menerima Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
      const json = await response.json();
      const data = json.data;
      const totalPage = data.meta.total_page;
      setTotalPage(totalPage);
      if (!data.banks) {
        setBankData(null);
      } else {
        setBankData(data);
      }
    } catch (error) {
      setBankData(null);
    } finally {
      setIsLoading(false);
    }
  }, [page, searchBankKeyword, navigate]);

  // =====================
  // Use Effect Method
  // =====================

  useEffect(() => {
    if (searchBankKeyword !== "") {
      getBankSearchData();
    } else {
      getBankData();
    }
  }, [searchBankKeyword, getBankData, getBankSearchData]);

  // =====================
  // Render Method
  // =====================

  return isLoading ? (
    <div className="loading-container">
      <div className="loader"></div>
      <p>Sedang Memuat Mohon Menunggu</p>
    </div>
  ) : (
    <div>
      <h1>Bank</h1>
      <BankHeader onAddBankClicked={addNewBank} onSearchBank={searchBank} />

      <BankTable
        page={page}
        banks={bankData.banks}
        onEditBankClicked={onEditBank}
        onDeleteBankClicked={onDeleteBank}
      />
      <PaginationFooter
        isPreviousActive={page > 1}
        isNextActive={page < totalPage}
        onNextClick={onNextPageClicked}
        onPrevClick={onPreviousPageClicked}
      />
      <ModalAddBank
        isShowModal={isOpenModalAddBank}
        onCloseModal={onCloseModal}
        onSubmitData={onSubmitNewBank}
        bankObject={selectedBankData}
        key={Date.now()}
      />

      <ModalDeleteConfirmation
        isShowModal={isOpenModalDeleteBank}
        onCloseModal={onCloseModalDeleteBank}
        onDeleteConfirmed={onDeleteBankConfirmed}
        idToDelete={idBankToDelete}
      />
    </div>
  );
};

export default BankList;
